// ── 탭 간 잠금 (PORTAL-12586) ────────────────────────────────────────
// 회의록을 여러 탭으로 띄우면 녹음·백업 큐(IndexedDB)를 함께 쓴다. 탭 로컬 상태로는
// 서로를 볼 수 없어, 같은 출처의 모든 탭이 공유하는 Web Locks 로 소유권을 가른다.
// 잠금은 탭이 닫히거나 죽으면 브라우저가 알아서 풀어 준다 — 남은 잠금이 영구히 막지 않는다.
// Web Locks 를 지원하지 않는 브라우저에선 막지 않는다(기존 동작 그대로).

const getLocks = (): LockManager | undefined =>
  typeof navigator !== 'undefined' ? navigator.locks : undefined;

/**
 * 잠금을 잡아 둔다 — 다른 탭이 이미 잡고 있으면 null.
 * 돌려받은 release 를 부를 때까지 잠금을 유지한다.
 */
export const holdLock = (name: string): Promise<(() => void) | null> => {
  const locks = getLocks();
  if (!locks) return Promise.resolve(() => undefined);
  return new Promise((resolve) => {
    locks
      .request(name, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(null);
          return undefined;
        }
        // 콜백이 돌려준 Promise 가 끝날 때까지 잠금이 유지된다
        return new Promise<void>((release) => resolve(() => release()));
      })
      .catch(() => resolve(() => undefined));
  });
};

/**
 * 잠금을 잡을 수 있을 때만 fn 을 실행한다 — 다른 탭이 이미 하고 있으면 건너뛴다.
 * ran 이 false 면 실행하지 않은 것이다.
 */
export const runWithLock = async <T>(
  name: string,
  fn: () => Promise<T>,
): Promise<{ ran: true; value: T } | { ran: false }> => {
  const release = await holdLock(name);
  if (!release) return { ran: false };
  try {
    return { ran: true, value: await fn() };
  } finally {
    release();
  }
};

/**
 * 어느 탭이든 이 잠금을 쥐고 있는가 — 판단할 수 없으면(미지원·오류) undefined.
 * 비정상 종료된 녹음인지, 다른 탭에서 아직 녹음 중인지 가를 때 쓴다 (PORTAL-12587).
 */
export const isLockHeld = async (
  name: string,
): Promise<boolean | undefined> => {
  const locks = getLocks();
  if (!locks?.query) return undefined;
  try {
    const snapshot = await locks.query();
    return (snapshot.held ?? []).some((lock) => lock.name === name);
  } catch {
    return undefined;
  }
};

/** 녹음 — 한 번에 한 탭만 */
export const RECORDING_LOCK = 'talkflow-recording';

/** 비정상 종료 녹음 되살리기 — 한 번에 한 탭만 (PORTAL-12587) */
export const LIVE_RECOVERY_LOCK = 'talkflow-live-recovery';

/** 백업 1건 재업로드 — 한 번에 한 탭만 */
export const queueRecordLock = (id: number) => `talkflow-audio-queue-${id}`;
